// @file Composable to manage tooltip display timing logic
import { debounce } from 'es-toolkit'
import { onScopeDispose } from 'vue'

interface TooltipTimerOptions<K> {
  /**
   * Delay in milliseconds before showing the tooltip after a hover event.
   * @default 1000
   */
  showDelayMs?: number
  /**
   * Delay in milliseconds before requiring a delay for the next tooltip show
   * after a hide event.
   * @default 2000
   */
  requireDelayTimeoutMs?: number
  onShow?: (key: K) => void
  onHide?: () => void
}

const useTooltipTimer = <K = string>(
  options: TooltipTimerOptions<K> = {},
): {
  requestShow: (key: K) => void
  requestHide: () => void
} => {
  const { showDelayMs = 1000, requireDelayTimeoutMs = 2000, onShow, onHide } = options

  let requestedKey: K | null = null
  let shouldDelayShowing = true
  let requireDelayTimer: ReturnType<typeof setTimeout>

  const debouncedShow = debounce((key: K): void => {
    // If still requested, show it
    if (requestedKey !== null && requestedKey === key) {
      onShow?.(requestedKey)
      // Subsequent requests shouldn't require a delay
      shouldDelayShowing = false
    }
  }, showDelayMs)

  const requestShow = (key: K): void => {
    requestedKey = key
    clearTimeout(requireDelayTimer)

    if (shouldDelayShowing) {
      debouncedShow(key)
    } else {
      // Show immediately
      onShow?.(key)
    }
  }

  const requestHide = (): void => {
    requestedKey = null
    // Hide immediately
    onHide?.()
    clearTimeout(requireDelayTimer)
    debouncedShow.cancel()

    // After a timeout, require delay again for next show
    requireDelayTimer = setTimeout(() => {
      shouldDelayShowing = true
    }, requireDelayTimeoutMs)
  }

  // Clean up timers on scope disposal
  onScopeDispose(() => {
    clearTimeout(requireDelayTimer)
    debouncedShow.cancel()
  })

  return {
    requestShow,
    requestHide,
  }
}

export { useTooltipTimer }
